/**
 * usePushSender — shared hook for push-to-Play functionality.
 *
 * Works across Editor (Tauri) and Web by using the EngineService
 * abstraction from PlatformService. All push operations go through
 * Engine JSON-RPC methods.
 */

import type { DiscoveredDevice } from "@magic-md/engine-client/protocol";
import { useT } from "@magic-md/i18n/react";
import { useCallback, useEffect } from "react";
import type { EngineService } from "../platform";
import { getPlatformService } from "../platform";
import { useEditorStore } from "../store/editor-store";
import { getNotificationBus } from "../store/notification-bus";
import { usePluginExecutionStore } from "../store/plugin-execution-store";
import { usePushClientStore } from "../store/push-store";
import { frontendLog } from "../utils/frontend-logger";

async function ensurePushNotificationBus(engine: EngineService): Promise<void> {
    try {
        await getNotificationBus().connect(engine);
    } catch (err) {
        frontendLog.error("[push] Notification listener failed", err, {
            source: "engine",
        });
    }
}

/**
 * Hook that manages the push sender lifecycle.
 * Listens for push events from the Engine and provides actions.
 */
export function usePushSender() {
    const t = useT("editor");
    const setConnected = usePushClientStore((s) => s.setConnected);
    const setPaired = usePushClientStore((s) => s.setPaired);
    const setPairingChallenge = usePushClientStore((s) => s.setPairingChallenge);
    const setPairingInProgress = usePushClientStore(
        (s) => s.setPairingInProgress,
    );
    const setLastPushStatus = usePushClientStore((s) => s.setLastPushStatus);

    // Listen for push events via NotificationBus
    useEffect(() => {
        const bus = getNotificationBus();

        const unsubs = [
            bus.subscribe<Record<string, unknown>>(
                "event/push.connectionChanged",
                (p) => {
                    const connected = p.connected as boolean;
                    setConnected(
                        connected,
                        p.deviceId as string | undefined,
                        p.deviceName as string | undefined,
                    );
                    if (!connected) {
                        setPaired(false);
                        setPairingChallenge(null);
                        // Clear stale device data — ports change on reconnect
                        usePushClientStore.getState().setDiscoveredDevices([]);
                        usePushClientStore.getState().setConnectingDeviceId(null);
                    }
                },
            ),
            bus.subscribe<Record<string, unknown>>(
                "event/push.pairingChallenge",
                (p) => {
                    setPairingChallenge(p.deviceName as string);
                    setPairingInProgress(false);
                },
            ),
            bus.subscribe<Record<string, unknown>>(
                "event/push.pairingResult",
                (p) => {
                    setPairingInProgress(false);
                    if (p.success) {
                        setPaired(true);
                        setPairingChallenge(null);
                    }
                },
            ),
            bus.subscribe<Record<string, unknown>>("event/push.ack", (p) => {
                setLastPushStatus(p.received ? "success" : "failed");
                setTimeout(() => {
                    usePushClientStore.getState().setLastPushStatus("idle");
                }, 2000);
            }),
            bus.subscribe<Record<string, unknown>>("event/push.syncStatus", (p) => {
                usePushClientStore.getState().setSyncing(p.syncing as boolean);
            }),
            bus.subscribe<Record<string, unknown>>("event/push.deviceFound", (p) => {
                const device = p.device as DiscoveredDevice;
                const current = usePushClientStore.getState().discoveredDevices;
                const exists = current.some((d) => d.deviceId === device.deviceId);
                if (!exists) {
                    usePushClientStore
                        .getState()
                        .setDiscoveredDevices([...current, device]);
                }
            }),
        ];

        try {
            const { engine } = getPlatformService();
            void ensurePushNotificationBus(engine);
        } catch (err) {
            frontendLog.error("[push] Failed to initialize notifications", err, {
                source: "engine",
            });
        }

        // Reconcile: query current push state in case events were
        // missed (page load while already connected, WS gap, etc.)
        (async () => {
            try {
                const { engine } = getPlatformService();
                const status = await engine.pushStatus?.();
                if (status?.connected) {
                    setConnected(
                        true,
                        status.remoteDevice?.deviceId,
                        status.remoteDevice?.deviceName,
                    );
                    if (status.paired) {
                        setPaired(true);
                    }
                    if (status.syncing) {
                        usePushClientStore.getState().setSyncing(true);
                    }
                }
            } catch {
                // Engine may not support push or not ready yet
            }
        })();

        return () => {
            for (const unsub of unsubs) unsub();
        };
    }, [
        setConnected,
        setPaired,
        setPairingChallenge,
        setPairingInProgress,
        setLastPushStatus,
    ]);

    /** Scan for Play devices on the local network */
    const scanDevices = useCallback(async () => {
        usePushClientStore.getState().setScanning(true);
        usePushClientStore.getState().setDiscoveredDevices([]);
        try {
            const { engine } = getPlatformService();
            await ensurePushNotificationBus(engine);
            const result = await engine.pushScan?.({ duration: 3000 });
            if (result) {
                usePushClientStore.getState().setDiscoveredDevices(result.devices);
            }
        } catch (err) {
            frontendLog.error("[push] Scan failed", err, { source: "ui" });
        } finally {
            usePushClientStore.getState().setScanning(false);
        }
    }, []);

    const connect = useCallback(async (address: string, port: number) => {
        const { engine } = getPlatformService();
        await ensurePushNotificationBus(engine);
        const result = await engine.pushConnect?.({ address, port });
        // After successful connect, reconcile state in case the
        // connectionChanged SSE event was missed during a WS gap
        if (result?.connected) {
            try {
                const status = await engine.pushStatus?.();
                if (status?.connected) {
                    usePushClientStore
                        .getState()
                        .setConnected(
                            true,
                            status.remoteDevice?.deviceId,
                            status.remoteDevice?.deviceName,
                        );
                }
            } catch {
                // Fallback: at minimum set connected from the RPC result
                usePushClientStore.getState().setConnected(true);
            }
        } else {
            usePushClientStore.getState().setConnectingDeviceId(null);
            throw new Error(t("push.error.cannotConnect"));
        }
    }, [t]);

    const disconnect = useCallback(async () => {
        const { engine } = getPlatformService();
        await engine.pushDisconnect?.();
        usePushClientStore.getState().reset();
    }, []);

    /** No-op: pairing is initiated by the Play device, not the Editor */
    const requestPairing = useCallback(async () => { }, []);

    const sendPairingCode = useCallback(
        async (code: string) => {
            setPairingInProgress(true);
            const { engine } = getPlatformService();
            await ensurePushNotificationBus(engine);
            await engine.pushPair?.({ code });
        },
        [setPairingInProgress],
    );

    const pushDocument = useCallback(async () => {
        const state = useEditorStore.getState();
        const focusedTabId = state.getFocusedTabId();
        const tab = state.tabs.find((t) => t.id === focusedTabId);
        if (!tab) return;

        const execStore = usePluginExecutionStore.getState();
        const sessionId =
            execStore.tabSessionMap.get(focusedTabId) ?? execStore.sessionId;

        setLastPushStatus("sending");
        try {
            const { engine } = getPlatformService();
            await ensurePushNotificationBus(engine);
            const result = await engine.pushSend?.({
                sessionId: sessionId ?? undefined,
                title: tab.title,
                content: tab.content,
            });
            const received = result?.received ?? false;
            setLastPushStatus(received ? "success" : "failed");
            setTimeout(() => {
                usePushClientStore.getState().setLastPushStatus("idle");
            }, 2000);
        } catch {
            setLastPushStatus("failed");
            setTimeout(() => {
                usePushClientStore.getState().setLastPushStatus("idle");
            }, 2000);
        }
    }, [setLastPushStatus]);

    const startSync = useCallback(async () => {
        const state = useEditorStore.getState();
        const focusedTabId = state.getFocusedTabId();
        const execStore = usePluginExecutionStore.getState();
        const sessionId =
            execStore.tabSessionMap.get(focusedTabId) ?? execStore.sessionId;
        if (!sessionId) return;

        usePushClientStore.getState().setSyncing(true);
        const { engine } = getPlatformService();
        await ensurePushNotificationBus(engine);
        await engine.pushSyncStart?.({ sessionId });
    }, []);

    /** No-op: sync updates are handled automatically by Engine on session/sync */
    const syncUpdate = useCallback(async () => { }, []);

    const stopSync = useCallback(async () => {
        usePushClientStore.getState().setSyncing(false);
        const { engine } = getPlatformService();
        await ensurePushNotificationBus(engine);
        await engine.pushSyncStop?.();
    }, []);

    return {
        scanDevices,
        connect,
        disconnect,
        requestPairing,
        sendPairingCode,
        pushDocument,
        startSync,
        syncUpdate,
        stopSync,
    };
}
